/**
 * @description Reads behind the Billing settings pages.
 *
 * One hook per endpoint rather than one hook per page. The two pages overlap —
 * both want the summary — and a per-page hook would fetch it twice or force one
 * page to pass it to the other. Sharing a query key means the second page reads
 * it from the cache.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import {
  changeMeteringPlan,
  getMeteringCreditPacks,
  getMeteringCredits,
  getMeteringInvoices,
  getMeteringLimits,
  getMeteringPlans,
  getMeteringSummary,
  getMeteringUsage,
  startMeteringCardChange
} from '@/services/DataService';
import type {
  CreditSort,
  InvoiceSort,
  MeteringPage,
  PlanChangeRequest,
  UsageGrouping,
  UsageWindow
} from '@/pages/settings/billing/types';

/**
 * How long a read stays fresh.
 *
 * Billing figures move on a metered event, not on a keystroke, and every read
 * here costs a call to the billing vendor — who bills and rate-limits per
 * request. A minute is short enough that a page revisited after using the
 * product shows the new figure, and long enough that switching between the two
 * billing pages is free.
 */
const STALE_MS = 60_000;

/**
 * How long the catalogue reads stay fresh.
 *
 * Longer than the figures. What a plan costs and what a pack is worth change
 * when somebody edits a catalogue, which is not something a reader of this page
 * can cause.
 */
const CATALOGUE_STALE_MS = 5 * 60_000;

/** The wallet, the plan, and the period's headline figures. */
export const useMeteringSummary = () => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: [ 'metering-summary' ],
    queryFn: getMeteringSummary,
    enabled: Boolean(isSignedIn),
    staleTime: STALE_MS
  });
};

/**
 * Per-series usage and spend over a window, and what the window before came to.
 *
 * The window and the split are in the key, so switching either refetches and
 * both readings stay cached — flipping back and forth is one call each way, not
 * one per flip.
 */
export const useMeteringUsage = (window: UsageWindow, groupBy: UsageGrouping) => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: [ 'metering-usage', window, groupBy ],
    queryFn: () => getMeteringUsage(window, groupBy),
    enabled: Boolean(isSignedIn),
    staleTime: STALE_MS,
    // Keeps the previous window's chart on screen while the next one loads,
    // rather than collapsing the plot to a skeleton on every chip press.
    placeholderData: keepPreviousData
  });
};

/** Each metered entitlement and how much of its allowance is gone. */
export const useMeteringLimits = () => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: [ 'metering-limits' ],
    queryFn: getMeteringLimits,
    enabled: Boolean(isSignedIn),
    staleTime: STALE_MS
  });
};

/** One page of the credit ledger, sorted and paged by the server. */
export const useMeteringCredits = (page: MeteringPage<CreditSort>) => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: [ 'metering-credits', page.page, page.page_size, page.sort, page.direction ],
    queryFn: () => getMeteringCredits(page),
    enabled: Boolean(isSignedIn),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData
  });
};

/** The plan catalogue, with the current plan marked. */
export const useMeteringPlans = () => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: [ 'metering-plans' ],
    queryFn: getMeteringPlans,
    enabled: Boolean(isSignedIn),
    staleTime: STALE_MS
  });
};

/** One page of issued invoices, sorted and paged by the server. */
export const useMeteringInvoices = (page: MeteringPage<InvoiceSort>) => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: [ 'metering-invoices', page.page, page.page_size, page.sort, page.direction ],
    queryFn: () => getMeteringInvoices(page),
    enabled: Boolean(isSignedIn),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData
  });
};

/**
 * The packs a top-up can be bought in.
 *
 * Cached longer than the rest: a price list changes when somebody edits the
 * gateway catalogue, not when the workspace spends anything, and this read costs
 * a call to the gateway every time it misses.
 */
export const useMeteringCreditPacks = () => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: [ 'metering-credit-packs' ],
    queryFn: getMeteringCreditPacks,
    enabled: Boolean(isSignedIn),
    staleTime: CATALOGUE_STALE_MS
  });
};

/**
 * Ask the server to move this workspace onto another plan.
 *
 * Every read on both billing pages is dropped afterwards, including when the
 * answer was "open a checkout". A checkout that is opened and abandoned changes
 * nothing, so the refetch costs a call and tells the truth either way — where
 * refetching only on success would leave a page stale behind a checkout that
 * did complete, since the browser is never told that it did.
 */
export const useChangePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PlanChangeRequest) => changeMeteringPlan(request),
    onSettled: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey[0]).startsWith('metering-')
      });
    }
  });
};

/**
 * Raise a gateway transaction for changing the card a switch will charge.
 *
 * Nothing is invalidated here: the returned transaction is opened in the
 * gateway's checkout, and the switch that follows is what re-reads the pages.
 */
export const useStartCardChange = () => {
  return useMutation({
    mutationFn: () => startMeteringCardChange()
  });
};
